
import React, { useState, useMemo } from 'react';
import { SONG_GRIDS } from '../../services/gscLibrary';
import { GSCMember, UserProfile } from '../../types';
import { api } from '../../services/api';
import { useGuitarMatch } from '../../context/GuitarMatchContext';

interface MemberHubProps {
    member: GSCMember;
    initialTab?: 'SONGBOOK' | 'INSTITUTION' | 'ARENA' | 'REWARDS';
    onSelectSong: (title: string, grid: string[]) => void;
    onClose: () => void;
    lang: 'en' | 'fr';
}

export const MemberHub: React.FC<MemberHubProps> = ({ initialTab = 'SONGBOOK', onSelectSong, onClose, lang }) => {
    const { userProfile, setIsPremium } = useGuitarMatch();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [schoolCode, setSchoolCode] = useState("");
    const [isLinking, setIsLinking] = useState(false);
    const [linkedSchool, setLinkedSchool] = useState<any>(JSON.parse(localStorage.getItem('gsc_school_data') || 'null'));

    const handleLinkSchool = async () => {
        setIsLinking(true);
        const res = await api.linkInstitution(schoolCode);
        if (res.success) {
            const data = { id: schoolCode, name: res.name, teacher: res.teacher };
            localStorage.setItem('gsc_school_id', schoolCode);
            localStorage.setItem('gsc_school_data', JSON.stringify(data));
            setLinkedSchool(data);
            setIsPremium(true); // L'abonnement école offre le premium
            alert(lang === 'fr' ? "Compte lié avec succès ! Accès Premium activé par votre école." : "Account linked! Premium access activated by your school.");
        } else {
            alert(lang === 'fr' ? "Code invalide." : "Invalid code.");
        }
        setIsLinking(false);
    };

    return (
        <div className="fixed inset-0 z-[10000] flex justify-end">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={onClose}></div>
            <div className="relative w-full max-w-2xl bg-[#010508] border-l border-white/10 h-full shadow-2xl flex flex-col animate-fade-in-left">
                
                <div className="p-8 bg-gradient-to-br from-[#001D26] to-black border-b border-white/5">
                    <div className="flex justify-between items-start mb-6">
                        <h2 className="text-4xl font-black text-white italic tracking-tighter uppercase leading-none">Espace <br/>Membre</h2>
                        <button onClick={onClose} className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-xl hover:bg-white/10 transition-colors">✕</button>
                    </div>

                    <div className="flex gap-2 bg-black/40 p-1.5 rounded-[24px] border border-white/5 w-fit">
                        {[
                            {id: 'SONGBOOK', label: 'Songbook', icon: '📚'}, 
                            {id: 'INSTITUTION', label: 'École', icon: '🎓'},
                            {id: 'REWARDS', label: 'Trophées', icon: '🎁'}
                        ].map(tab => (
                            <button 
                                key={tab.id} 
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`px-6 py-2.5 rounded-full text-[10px] font-black uppercase transition-all flex items-center gap-2 ${activeTab === tab.id ? 'bg-white text-black shadow-lg' : 'text-slate-500 hover:text-white'}`}
                            >
                                <span>{tab.icon}</span> {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
                    {activeTab === 'SONGBOOK' && (
                        <div className="grid grid-cols-1 gap-4">
                            {Object.entries(SONG_GRIDS).map(([title, grid]) => (
                                <button key={title} onClick={() => onSelectSong(title, grid)} className="w-full p-6 bg-white/5 hover:bg-white/10 rounded-3xl border border-white/5 text-left flex items-center justify-between group transition-all">
                                    <div>
                                        <h4 className="text-xl font-black text-white uppercase italic tracking-tighter group-hover:text-smart-gold">{title}</h4>
                                        <div className="flex gap-2 mt-1">{grid.slice(0, 3).map((c, i) => <span key={i} className="text-[8px] font-mono bg-black/60 px-2 py-0.5 rounded text-slate-500">{c}</span>)}</div>
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-smart-gold group-hover:text-black">▶</div>
                                </button>
                            ))}
                        </div>
                    )}

                    {activeTab === 'INSTITUTION' && (
                        <div className="space-y-8 animate-fade-in">
                            {linkedSchool ? (
                                <div className="p-8 rounded-[40px] bg-emerald-500/10 border-2 border-emerald-500/30 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center text-3xl">🎓</div>
                                        <div>
                                            <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">{linkedSchool.name}</h3>
                                            <p className="text-xs font-black text-emerald-400 uppercase tracking-widest">Élève de : {linkedSchool.teacher}</p>
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t border-emerald-500/20">
                                        <p className="text-sm text-slate-300 italic">Vos rapports de progression sont automatiquement transmis à votre professeur pour le cours de la semaine prochaine.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="space-y-2 text-center">
                                        <h3 className="text-2xl font-black text-white italic uppercase">Lier mon École</h3>
                                        <p className="text-sm text-slate-500 italic">Entrez le code fourni par votre professeur.</p>
                                    </div>
                                    <div className="bg-white/5 p-8 rounded-[40px] border border-white/10 space-y-4">
                                        <input 
                                            type="text" 
                                            value={schoolCode}
                                            onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                                            placeholder="EX: CONSERV-PARIS" 
                                            className="w-full bg-black/60 border-2 border-white/10 rounded-2xl px-6 py-4 text-center text-xl font-black text-smart-gold outline-none focus:border-smart-gold" 
                                        />
                                        <button 
                                            onClick={handleLinkSchool}
                                            disabled={isLinking || schoolCode.length < 5}
                                            className="w-full py-5 bg-white text-black rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-smart-gold transition-all disabled:opacity-30"
                                        >
                                            {isLinking ? "VÉRIFICATION..." : "ACTIVER MON ACCÈS ÉCOLE ⚡"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-8 border-t border-white/5 bg-black flex justify-center">
                    <p className="text-[8px] font-black text-slate-800 uppercase tracking-[0.6em]">GSC Institutional Gateway • industrial Edition</p>
                </div>
            </div>
        </div>
    );
};
